import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { ArrowRight } from "@phosphor-icons/react";

const EMBLEM = "https://static.prod-images.emergentagent.com/jobs/e7aa276a-5de1-4c83-900c-894e1c7268a2/images/df5b7e9d7d3c0712e0fada815b5b10f3496a96f18ade7bc4231629bb9c81c851.jpeg";
const LOCKUP = "https://customer-assets-rejwkqb3.emergentagent.net/job_proposal-scorecard/artifacts/mxac6slj_proposalforensics.png";
const inputCls = "mt-2 w-full h-11 px-3.5 rounded-md border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.company && !data.company.onboarded ? "/onboarding" : "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative bg-black grain flex-col justify-between p-14 text-white">
        <img src={LOCKUP} alt="Proposal Forensics" className="w-full max-w-sm -ml-2" />
        <div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-[1.05]">Smarter proposals.<br />Bigger wins.</h1>
          <p className="mt-6 text-slate-400 max-w-md text-base leading-relaxed">Proposal intelligence for AV, automation, lighting, shading, networking & security integrators. Analyze. Optimize. Approve.</p>
        </div>
        <p className="font-mono-plex text-[11px] uppercase tracking-[0.25em] text-slate-500">Confidential · Business Intelligence</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-[#FAFAFA] grain">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <div className="inline-flex bg-black rounded-lg p-2 mb-6 lg:hidden">
              <img src={EMBLEM} alt="Proposal Forensics" className="h-10 w-10 object-contain" />
            </div>
            <h2 className="font-display font-extrabold text-3xl tracking-tight text-[#0F172A]">Sign in</h2>
            <p className="text-slate-500 mt-2 text-sm">Welcome back to Proposal Forensics.</p>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Email</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" className={inputCls} />
            </div>
            <div>
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Password</label>
              <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className={inputCls} />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-slate-500 hover:text-[#0F172A] transition-colors">Forgot password?</Link>
              </div>
            </div>
            {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
            <button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {loading ? "Signing in…" : "Sign in"}{!loading && <ArrowRight size={16} weight="bold" />}
            </button>
          </form>
          <p className="mt-6 text-sm text-slate-500 text-center">
            New to Proposal Forensics?{" "}
            <Link to="/register" data-testid="go-register-link" className="font-semibold text-[#0F172A] hover:underline">Create a company account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
